import React, { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import _ from 'underscore';
import LanguageStringResolver from '../../fields/language/LanguageStringResolver';
import ApplicationToast from '../../inAppMessages/ApplicationToast';
import { toastActions } from '../../inAppMessages/toastActions';
import LANGUAGES_CONTROLLER from '../../../controllers/languages';
import ReadOnlyWrapper from '../../authorization/ReadOnlyWrapper';
import BulkOperationConfirmationModal from './BulkOperationConfirmationModal';
import BulkOperationStatusModal from './BulkOperationStatusModal';
import { API } from '../../../controllers/bulkActionStatus';
import { OperationTypes } from '../../../utils/enums';
import Logger, { ALL_MESSAGES, LOG_LEVELS } from '../../../utils/logger';

const log = new Logger('BULK_OPERATION_ACTIONS', LOG_LEVELS.DEBUG);

export const BulkOperationActionPanel = ({
    documentList,
    selectedIds,
    onRequestAction,
    onActionSubmitted,
    showMessages,
    customerId,
    userName,
    operations,
    doGetBulkOperationStatus,
    languages,
    doAddToast,
    doRemoveToast
}) => {
    const [pendingAction, setPendingAction] = useState(null);
    const [showBulkOperationStatusModal, setShowBulkOperationStatusModal] = useState(false);

    const operationsRef = useRef(operations);
    useEffect(() => {
        operationsRef.current = operations;
    }, [operations]);

    const selectedCount = selectedIds.length;

    const handleAction = (action) => {
        log.clearAllDictionaryErrors && log.clearAllDictionaryErrors();
        setPendingAction({ action, ids: selectedIds });
    };

    const handleConfirm = () => {
        setPendingAction(null);
        onActionSubmitted && onActionSubmitted();
    };

    const handleCancel = () => {
        setPendingAction(null);
    };

    const handleGetBulkOperationStatus = () => {
        doGetBulkOperationStatus(
            customerId,
            userName,
            () => {
                log.debug('Success fetching Operations Status');

                if (operationsRef.current?.length >= 0) {
                    setShowBulkOperationStatusModal(true);
                } else {
                    log.setDictionaryMessage(ALL_MESSAGES.BULK_OPERATION_STATUS_NODATA);
                }
            },
            (errors) => {
                log.debug(`Error fetching operations: ${errors}`);
                doAddToast(
                    'bulk-operation-status-error',
                    LANGUAGES_CONTROLLER.format(languages, 'inAppMessage_App_Error_171'),
                    '',
                    'error'
                );
            }
        );
    };

    const previewList = pendingAction && _.flatten(
        (documentList || [])
            .filter(item => item.childRows)
            .map(item => item.childRows)
    ).filter(childDoc => pendingAction.ids.findIndex(obj => obj.id === childDoc.id) >= 0);

    return (
        <ReadOnlyWrapper>
            <div>
                {showMessages && (
                    <ApplicationToast scope="uploadedDocuments" testId="bulk-operation-toasts" onDismiss={doRemoveToast} />
                )}
                <div className="uploaded-documents-bulk-action-panel">
                    <button
                        name="Status"
                        className="secondary icon-leading"
                        data-testid="status-button"
                        onClick={handleGetBulkOperationStatus}
                    >
                        <s-icon name="alert-triangle"></s-icon>
                        <LanguageStringResolver
                            string="uploadedDocuments_checkBulkOperationFailures"
                            tooltip="uploadedDocuments_checkBulkOperationFailures_Extended"
                        />
                    </button>
                    <button
                        disabled={selectedCount === 0 ? 'disabled' : ''}
                        className={'secondary icon-leading'}
                        onClick={() => handleAction('export')}
                        excludeFromReadonly={true}
                    >
                        <s-icon name="export"></s-icon>
                        <LanguageStringResolver string="documentDetails_exportSelectedDocuments" />
                    </button>
                    <button
                        disabled={selectedCount === 0 ? 'disabled' : ''}
                        className={'secondary icon-leading'}
                        onClick={() => handleAction('delete')}
                    >
                        <s-icon name="trash"></s-icon>
                        <LanguageStringResolver string="documentDetails_deleteSelectedDocuments" />
                    </button>
                    <button
                        disabled={selectedCount === 0 ? 'disabled' : ''}
                        className={'secondary icon-leading'}
                        onClick={() => handleAction('redeliver')}
                    >
                        <s-icon name="redo"></s-icon>
                        <LanguageStringResolver string="documentDetails_redeliverSelectedDocuments" />
                    </button>
                </div>
            </div>
            {pendingAction ? (
                <BulkOperationConfirmationModal
                    isOpen={true}
                    action={pendingAction.action}
                    documents={previewList}
                    filename={'UploadedDocumentsLog'}
                    languages={languages}
                    onRequestAction={onRequestAction}
                    onConfirm={handleConfirm}
                    onCancel={handleCancel}
                />
            ) : null}
            <BulkOperationStatusModal
                showModal={showBulkOperationStatusModal}
                onCloseModal={() => setShowBulkOperationStatusModal(false)}
                operations={operations}
            />
        </ReadOnlyWrapper>
    );
};


BulkOperationActionPanel.defaultProps = {
    documentList: [],
    selectedIds: [],
    onRequestAction: null,
    onActionSubmitted: null,
    showMessages: true,
    operations: []
};

function mapStateToProps(state) {
    return {
        userName: state.authentication?.data?.userName || 'Unknown',
        customerId: state.authentication?.data?.customerId || [],
        operations: Array.isArray(state.bulkActionStatus?.operations) ? state.bulkActionStatus.operations : [],
        languages: state.languages
    };
}

function mapDispatchersToProps(dispatch) {
    return {
        doGetBulkOperationStatus: (tenantId, userId, successes, errors) => {
            const operationTypeIds = [
                OperationTypes.BulkDeleteFiles,
                OperationTypes.BulkRedeliverFiles
            ];
            dispatch(API.getOperations(tenantId, userId, operationTypeIds, successes, errors));
        },
        doAddToast: (id, title, message, type) => {
            dispatch(toastActions.addToast(id, title, message, type, 'uploadedDocuments'));
        },
        doRemoveToast: (id) => {
            dispatch(toastActions.removeToast(id));
        }
    };
}

export default connect(mapStateToProps, mapDispatchersToProps)(BulkOperationActionPanel);
